import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"

import { ellipsis } from "@f2e/shared/utils"
import { Badge } from "@f2e/ui/components/base"
import { UserAvatar, PageTable } from "@/components"
import { PermissionGuard } from "@/components/Auth"
import { RoleSelect } from "@/components/User"
import {
  Workspace,
  Member,
  WorkspaceRole,
  Organization,
  PageInfo,
  Permission,
  Scope,
} from "@/types"
import { useStore } from "@/store"
import { useUpdateWorkspaceMemberRoleApi } from "@/hooks/api"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"

import { MemberActionButton } from "./MemberActionButton"

export interface MemberTableProps {
  workspace: Workspace
  members: Member[]
  pageInfo: PageInfo
  onPageChange?: (page: number) => void
  onRoleChange?: (member: Member, role: WorkspaceRole) => void
  onLeave?: (member: Member) => void
  onError?: (error: Error) => void
}

const MemberTable = ({
  workspace,
  members,
  pageInfo,
  onPageChange,
  onRoleChange,
  onLeave,
  onError,
}: MemberTableProps) => {
  const [userMe, currentOrganization] = useStore((state) => [
    state.userMe,
    state.currentOrganization,
  ])
  const [sortedMembers, setSortedMembers] = useState<Member[]>([])
  const { t } = useTranslation()

  useEffect(() => {
    if (members) {
      setSortedMembers(
        [...members].sort((a) => (a.id === workspace.owner.id ? -1 : 1))
      )
    }
  }, [members, workspace])

  if (!currentOrganization) return null

  return (
    <PageTable<Member>
      className="mt-4"
      columns={[
        {
          title: `${t("workspace.member.memberCount", { count: pageInfo.totalRecords })}`,
          width: "34%",
          render: (member) => (
            <div className="flex">
              <div className="flex items-center">
                <UserAvatar user={member} className="h-10 w-10" />
                <div className="grid grid-rows-2 ml-4">
                  <div className="text-base">
                    <span>{ellipsis(member.name, 30)}</span>
                    <span>
                      {userMe?.id === member.id && (
                        <Badge variant="secondary" className="ml-2 py-0">
                          {t("common.you")}
                        </Badge>
                      )}
                    </span>
                  </div>
                  <div className="text-sm text-slate-500">{member.email}</div>
                </div>
              </div>
            </div>
          ),
        },
        {
          title: t("workspace.member.role"),
          width: "17%",
          render: (member) => (
            <PermissionGuard
              permission={Permission.WsUpdateRole}
              workspace={workspace}
              disabled
            >
              <MemberRoleSelect
                member={member}
                workspace={workspace}
                organization={currentOrganization}
                onChange={(role) => onRoleChange?.(member, role)}
                onError={onError}
              />
            </PermissionGuard>
          ),
        },
        {
          title: t("workspace.actions"),
          width: "17%",
          render: (member) => (
            <MemberActionButton
              workspace={workspace}
              member={member}
              onLeave={onLeave}
            />
          ),
        },
      ]}
      data={sortedMembers}
      defaultPage={pageInfo.current}
      totalPages={pageInfo.totalPages}
      onPageChange={onPageChange}
    />
  )
}

const MemberRoleSelect = ({
  member,
  workspace,
  organization,
  disabled = false,
  onChange,
  onError,
}: {
  member: Member
  workspace: Workspace
  organization: Organization
  disabled?: boolean
  onChange?: (role: WorkspaceRole) => void
  onError?: (error: Error) => void
}) => {
  const {
    error: updateUserRoleError,
    data: updateUserRoleResp,
    fetchFn: updateUserRole,
  } = useUpdateWorkspaceMemberRoleApi(organization.id, workspace.id, member.id)

  const { t } = useTranslation()
  const [selectedRole, setSelectedRole] = useState<WorkspaceRole>(
    member.role as WorkspaceRole
  )

  useEffect(() => {
    setSelectedRole(member.role as WorkspaceRole)
  }, [member.role])

  useEffect(() => {
    if (!updateUserRoleError) return
    setSelectedRole(member.role as WorkspaceRole)
    onError?.(
      new Error(
        t("workspace.memberTable.table.roleChangeFailed.description", {
          message: getUserFacingApiErrorMessage(
            updateUserRoleError,
            t,
            updateUserRoleResp?.message || ""
          ),
        })
      )
    )
  }, [onError, updateUserRoleError, updateUserRoleResp, t, member.role])

  const handleRoleChange = (role: WorkspaceRole) => {
    setSelectedRole(role)
    updateUserRole({
      jsonBody: {
        user_id: member.id,
        role_name: role,
      },
      callbackFn: (resp) => {
        if (!resp || !resp.data) return
        onChange?.(role)
      },
    })
  }

  return (
    <RoleSelect
      value={selectedRole}
      mode={Scope.Workspace}
      readOnly={disabled}
      onChange={(role) => handleRoleChange(role as WorkspaceRole)}
    />
  )
}

export { MemberTable }
export default MemberTable
